import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signInWithPassword: async () => {},
  signOut: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Guard in case supabase client wasn't configured (missing env vars on deploy)
    if (!supabase || typeof supabase.auth === 'undefined') {
      console.warn('Supabase client not initialized. Auth will be disabled.');
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    // try to get existing session
    (async () => {
      try {
        const result = await supabase.auth.getSession();
        if (!isMounted) return;
        const data = result?.data ?? {};
        setSession(data.session ?? null);
        setUser(data.session?.user ?? null);
      } catch (err) {
        console.error('Error getting supabase session:', err);
        setSession(null);
        setUser(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      try {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      } catch (err) {
        console.error('Error handling auth state change:', err);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      try {
        // unsubscribe if possible
        subscription?.subscription?.unsubscribe?.();
      } catch (err) {
        // ignore
      }
    };
  }, []);

  const signInWithPassword = async ({ email, password }) => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (!supabase || typeof supabase.auth === 'undefined') {
      throw new Error('Auth not available');
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = useMemo(
    () => ({ user, session, loading, signInWithPassword, signOut }),
    [user, session, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);


